import { z } from 'zod';
import { revisionColorSchema, versionFormatSchema } from './colors';
import { revisionLabelSchema, revisionSchemeSchema } from './scheme';

const pagesSchema = z
  .array(z.number().int().positive())
  .min(1)
  .refine((pages) => new Set(pages).size === pages.length, { message: 'a page is listed twice' });

/**
 * `revisions.yaml`, what the agent writes for its run's version: the scheme, the format and the rows, each a label
 * (a color key under `color`; the mark's text under `custom`; left out for the unmarked set of a custom collated copy
 * and for the one `changed` row) with the physical pages carrying it. Each page belongs to one row. Whether a row is
 * new is not the agent's to say: the database works it out. `scheme: unknown` fails on the `scheme` path; under
 * `color` a label outside the keys (`unknown` included) is `invalid_value` on its `label` path.
 */
export const revisionFileSchema = z
  .strictObject({
    scheme: revisionSchemeSchema,
    format: versionFormatSchema,
    rows: z
      .array(z.strictObject({ label: z.string().trim().optional(), pages: pagesSchema }))
      .min(1),
  })
  .superRefine((file, ctx) => {
    const row = (i: number, field: 'label' | 'pages' | null) =>
      field === null ? ['rows', i] : ['rows', i, field];
    const labels = file.rows.map((r) => r.label);
    if (file.scheme === 'color') {
      labels.forEach((label, i) => {
        if (label === undefined)
          ctx.addIssue({
            code: 'custom',
            path: row(i, null),
            message: 'a color row needs a label',
          });
        else if (!revisionColorSchema.safeParse(label).success)
          ctx.addIssue({
            code: 'invalid_value',
            values: revisionColorSchema.options,
            input: label,
            path: row(i, 'label'),
            message: 'not a revision color key',
          });
      });
      if (new Set(labels).size !== labels.length)
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'a color is listed twice' });
    } else if (file.scheme === 'custom') {
      labels.forEach((label, i) => {
        if (label !== undefined && !revisionLabelSchema.safeParse(label).success)
          ctx.addIssue({
            code: 'custom',
            path: row(i, 'label'),
            message: 'a label is 1 to 40 characters',
          });
      });
      if (labels.filter((l) => l === undefined).length > 1)
        ctx.addIssue({
          code: 'custom',
          path: ['rows'],
          message: 'at most one row without a label',
        });
      const keys = labels.flatMap((l) => (l === undefined ? [] : [l.toLowerCase()]));
      if (new Set(keys).size !== keys.length)
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'a label is listed twice' });
    } else {
      if (file.rows.length !== 1)
        ctx.addIssue({ code: 'custom', path: ['rows'], message: 'one row of changed pages' });
      labels.forEach((label, i) => {
        if (label !== undefined)
          ctx.addIssue({
            code: 'custom',
            path: row(i, 'label'),
            message: 'a changed row has no label',
          });
      });
    }
    const pages = file.rows.flatMap((r) => r.pages);
    if (new Set(pages).size !== pages.length)
      ctx.addIssue({ code: 'custom', path: ['rows'], message: 'a page is under two rows' });
  });
export type RevisionFile = z.infer<typeof revisionFileSchema>;
